import { useMemo, useRef, useState } from 'react';
import {
  useAppConfig, useAttendanceStatus, useFasAttendants, useImportAttendants, useLinkAttendant, useSyncFas, useUsers,
} from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';
import { Avatar, EmptyState, Modal, TableSkeleton } from '@/components/ui';
import { IconCheck, IconCopy, IconRefresh } from '@/components/Icons';
import { formatAccessMessage } from '@/lib/access';
import { copyText } from '@/lib/clipboard';
import { FAS_ROLE_LABELS, formatISODateTime, formatNumber } from '@/lib/format';
import { isValidLogin } from '@/lib/login';
import type { FasAttendant, ImportedMember } from '@/lib/types';

/**
 * A equipe da conta da operação na For All Sales, para o administrador
 * escolher: criar como membro (com senha provisória), vincular a um membro que
 * já existe, ou deixar de fora. Também mostra a situação da leitura.
 */
export function FasTeamModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const { data: status } = useAttendanceStatus();
  const enabled = status?.enabled ?? false;
  const { data: attendants, isLoading } = useFasAttendants({ enabled });
  const { data: users } = useUsers({ includeInactive: 'true' });
  const sync = useSyncFas();
  const link = useLinkAttendant();
  const importMembers = useImportAttendants();

  const [selected, setSelected] = useState<Record<number, string>>({});
  const [onlyActive, setOnlyActive] = useState(true);
  const [created, setCreated] = useState<ImportedMember[] | null>(null);

  const linkedUserIds = useMemo(() => new Set((attendants ?? []).map((a) => a.member?.id).filter(Boolean)), [attendants]);
  const freeUsers = (users ?? []).filter((user) => !linkedUserIds.has(user.id));
  const visible = (attendants ?? []).filter((a) => !onlyActive || a.active || a.member);
  const chosen = Object.entries(selected).map(([id, login]) => ({ attendantId: Number(id), login: login.trim().toLowerCase() }));
  const invalid = chosen.some((item) => !isValidLogin(item.login));

  function toggle(attendant: FasAttendant) {
    setSelected((current) => {
      const next = { ...current };
      if (attendant.id in next) delete next[attendant.id];
      else next[attendant.id] = attendant.suggestedLogin;
      return next;
    });
  }

  async function runSync(full: boolean) {
    try {
      await sync.mutateAsync({ full });
      toast.success(full ? 'Releitura completa pedida.' : 'Leitura pedida. Os números se atualizam em instantes.');
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  async function linkTo(attendant: FasAttendant, userId: string | null) {
    try {
      const result = await link.mutateAsync({ attendantId: attendant.id, userId });
      if (!userId) toast.success(`${attendant.name} desvinculado.`);
      else toast.success(result.owners > 0 ? `Vinculado. ${formatNumber(result.owners)} cliente(s) passaram para o membro.` : 'Vinculado.');
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  async function createMembers() {
    try {
      const result = await importMembers.mutateAsync(chosen);
      setCreated(result.created);
      setSelected({});
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  if (created) return <CreatedMembers created={created} onClose={onClose} />;

  return (
    <Modal
      title="Equipe da For All Sales"
      width={980}
      onClose={onClose}
      footer={
        <>
          <span className="muted" style={{ fontSize: 12.5, marginRight: 'auto' }}>
            {chosen.length > 0 ? `${chosen.length} para criar como membro` : 'Marque quem deve ganhar acesso à plataforma.'}
          </span>
          <button type="button" className="btn" onClick={onClose}>Fechar</button>
          <button type="button" className="btn btn-primary" disabled={chosen.length === 0 || invalid || importMembers.isPending} onClick={() => void createMembers()}>
            {importMembers.isPending ? <span className="spinner" /> : null}
            Criar {chosen.length > 1 ? `${chosen.length} membros` : 'membro'}
          </button>
        </>
      }
    >
      <SyncStrip onSync={(full) => void runSync(full)} busy={sync.isPending} />

      {!enabled ? null : isLoading && !attendants ? (
        <TableSkeleton rows={5} columns={5} />
      ) : (attendants ?? []).length === 0 ? (
        <EmptyState message="A equipe aparece aqui depois da primeira leitura da For All Sales." />
      ) : (
        <>
          <label className="chip" style={{ marginBottom: 10 }}>
            <input type="checkbox" checked={onlyActive} onChange={(event) => setOnlyActive(event.target.checked)} />
            Só quem está ativo na For All Sales
          </label>
          <div className="table-wrap">
            <table className="tbl tbl-cards">
              <thead>
                <tr>
                  <th style={{ width: 32 }} />
                  <th>Na For All Sales</th>
                  <th className="right">Conversas</th>
                  <th>Última mensagem</th>
                  <th style={{ width: 300 }}>Na plataforma</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((attendant) => {
                  const picked = attendant.id in selected;
                  return (
                    <tr key={attendant.id}>
                      <td className="cell-pos">
                        {attendant.member ? (
                          <span className="positive" title="Já é membro"><IconCheck /></span>
                        ) : (
                          <input type="checkbox" checked={picked} onChange={() => toggle(attendant)} aria-label={`Criar ${attendant.name} como membro`} />
                        )}
                      </td>
                      <td className="cell-main">
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <Avatar name={attendant.name} color="#4FAE93" photo={attendant.avatarUrl} size={24} />
                          <span style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 500 }}>
                              {attendant.name}
                              {!attendant.active ? <span className="chip chip-negative" style={{ marginLeft: 6 }}>inativo</span> : null}
                            </div>
                            <div className="muted" style={{ fontSize: 12 }}>
                              {[attendant.email, attendant.attendanceRole ? FAS_ROLE_LABELS[attendant.attendanceRole] ?? attendant.attendanceRole : null, attendant.squads]
                                .filter(Boolean)
                                .join(' · ')}
                            </div>
                          </span>
                        </span>
                      </td>
                      <td className="num right cell-key" data-label="Conversas">{formatNumber(attendant.conversations)}</td>
                      <td className="num muted nowrap cell-full" data-label="Última mensagem">{attendant.lastActivityAt ? formatISODateTime(attendant.lastActivityAt) : '—'}</td>
                      <td className="cell-full" data-label="Na plataforma">
                        {attendant.member ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <span className="chip chip-positive">{attendant.member.name} · {attendant.member.login}</span>
                            <button type="button" className="link-btn muted" style={{ fontSize: 12 }} disabled={link.isPending} onClick={() => void linkTo(attendant, null)}>
                              desvincular
                            </button>
                          </span>
                        ) : picked ? (
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                            <span className="muted">Login</span>
                            <input
                              className="inp"
                              style={{ height: 30 }}
                              value={selected[attendant.id]}
                              onChange={(event) => setSelected((current) => ({ ...current, [attendant.id]: event.target.value }))}
                              aria-invalid={!isValidLogin(selected[attendant.id]!.trim().toLowerCase())}
                            />
                          </label>
                        ) : (
                          <select
                            className="inp"
                            style={{ height: 30, fontSize: 12.5 }}
                            value=""
                            disabled={link.isPending}
                            onChange={(event) => event.target.value && void linkTo(attendant, event.target.value)}
                            aria-label={`Vincular ${attendant.name} a um membro que já existe`}
                          >
                            <option value="">
                              {attendant.suggestedMember ? `Vincular a ${attendant.suggestedMember.name} (mesmo e-mail)?` : 'Vincular a um membro que já existe…'}
                            </option>
                            {attendant.suggestedMember ? <option value={attendant.suggestedMember.id}>{attendant.suggestedMember.name} · {attendant.suggestedMember.login}</option> : null}
                            {freeUsers
                              .filter((user) => user.id !== attendant.suggestedMember?.id)
                              .map((user) => <option key={user.id} value={user.id}>{user.name} · {user.login}</option>)}
                          </select>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
            Membro criado daqui já nasce vinculado: os leads que a pessoa atende na For All Sales entram em Clientes com ela
            como responsável, e os números de atendimento aparecem na página dela. Quem tem o mesmo e-mail é vinculado sozinho.
          </p>
        </>
      )}
    </Modal>
  );
}

function SyncStrip({ onSync, busy }: { onSync: (full: boolean) => void; busy: boolean }) {
  const { data: status } = useAttendanceStatus();
  if (!status) return null;
  if (!status.enabled) {
    return (
      <div className="alert alert-info">
        <span>
          A leitura da For All Sales está desligada: falta <code>FAS_DATABASE_URL</code> no servidor. Com ela, a plataforma
          lê (só leitura) a conta de <strong>{status.accountEmail}</strong> a cada poucos minutos.
        </span>
      </div>
    );
  }
  return (
    <div className="import-strip" style={{ margin: '0 0 14px' }}>
      <span>
        Conta <strong>{status.accountName ?? status.accountEmail}</strong>
        {' · '}
        {status.running ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span className="spinner" /> lendo agora…</span>
        ) : status.lastSuccessAt ? (
          `lida em ${formatISODateTime(status.lastSuccessAt)}`
        ) : (
          'ainda não lida'
        )}
      </span>
      <span className="muted">
        {formatNumber(status.counts.attendants)} na equipe · {formatNumber(status.counts.linked)} vinculados ·{' '}
        {formatNumber(status.counts.conversations)} conversas · {formatNumber(status.counts.leads)} clientes da For All Sales
      </span>
      {status.lastError ? <span className="negative" style={{ flexBasis: '100%' }}>{status.lastError}</span> : null}
      <span className="spacer" />
      <button type="button" className="btn btn-sm" disabled={busy || status.running} onClick={() => onSync(false)}>
        <IconRefresh /> Ler agora
      </button>
      <button type="button" className="btn btn-sm btn-ghost" disabled={busy || status.running} onClick={() => onSync(true)} title="Relê tudo, inclusive mudanças sem mensagem nova">
        Releitura completa
      </button>
    </div>
  );
}

/** As senhas provisórias aparecem uma vez só: copiar e mandar para cada pessoa. */
function CreatedMembers({ created, onClose }: { created: ImportedMember[]; onClose: () => void }) {
  const toast = useToast();
  const { data: config } = useAppConfig();
  const containerRef = useRef<HTMLDivElement>(null);
  const url = config?.appUrl ?? window.location.origin;
  const messageOf = (item: ImportedMember) =>
    formatAccessMessage({ name: item.user.name, login: item.user.login, email: item.user.email ?? '', password: item.password, url });

  async function copy(text: string) {
    const ok = await copyText(text, containerRef.current ?? document.body);
    if (ok) toast.success('Copiado.');
    else toast.error('Não foi possível copiar; selecione o texto e copie à mão.');
  }

  return (
    <Modal
      title={created.length === 1 ? 'Membro criado' : `${created.length} membros criados`}
      width={720}
      onClose={onClose}
      footer={
        <>
          {created.length > 1 ? (
            <button type="button" className="btn" onClick={() => void copy(created.map(messageOf).join('\n\n———\n\n'))}>
              <IconCopy /> Copiar todos
            </button>
          ) : null}
          <button type="button" className="btn btn-primary" onClick={onClose}>Pronto</button>
        </>
      }
    >
      <div ref={containerRef}>
        <div className="alert alert-info" style={{ marginBottom: 14 }}>
          <span>
            Estas senhas provisórias aparecem <strong>só agora</strong>. Copie o acesso e mande para cada pessoa; ela troca a
            senha em "Minha conta" depois de entrar.
          </span>
        </div>
        {created.map((item) => (
          <div key={item.user.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: '1px solid var(--border)' }}>
            <Avatar name={item.user.name} color={item.user.avatarColor} photo={item.user.photoUrl} size={28} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 500 }}>{item.user.name}</div>
              <div className="muted num" style={{ fontSize: 12.5 }}>
                usuário <strong>{item.user.login}</strong> · senha <strong style={{ fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.02em' }}>{item.password}</strong>
              </div>
            </div>
            <button type="button" className="btn btn-sm" onClick={() => void copy(messageOf(item))}>
              <IconCopy /> Copiar acesso
            </button>
          </div>
        ))}
      </div>
    </Modal>
  );
}
